import { useEffect, useRef, useState, useCallback } from "react";
import { safeHtml } from "@/lib/html";
import { useParams, useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { SafetyBadge, VerifiedStatusBadge, DiffView, ConfidenceBar, ReliabilityBadge, Spinner } from "@/components/bits";
import {
  ArrowLeft, ExternalLink, Search, ShieldCheck, CheckCircle2, XCircle, UploadCloud,
  MousePointerSquareDashed, FileText, AlertTriangle, Quote, RefreshCw,
} from "lucide-react";

const TYPE_COLORS = {
  Official: "bg-emerald-100 text-emerald-800",
  Government: "bg-blue-100 text-blue-800",
  News: "bg-amber-100 text-amber-800",
  Education: "bg-slate-100 text-slate-700",
  Other: "bg-slate-100 text-slate-700",
};

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, mode, wp } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [command, setCommand] = useState("");
  const [presets, setPresets] = useState([]);

  const loadPresets = useCallback(() => {
    client.get("/instruction-presets").then((r) => setPresets(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const savePreset = async () => {
    const text = command.trim();
    if (!text) return;
    try { await client.post("/instruction-presets", { text }); loadPresets(); toast.success("Instruction saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deletePreset = async (pid) => {
    try { await client.delete(`/instruction-presets/${pid}`); setPresets((p) => p.filter((x) => x.id !== pid)); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const [acting, setActing] = useState(false);
  const [note, setNote] = useState("");
  const contentRef = useRef(null);
  const lastEl = useRef(null);

  useEffect(() => {
    client.get(`/wp/posts/${id}`).then((r) => setPost(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [id]);

  const selectBlock = useCallback((el, i) => {
    if (lastEl.current) lastEl.current.classList.remove("ez-selected");
    el.classList.add("ez-selected");
    lastEl.current = el;
    setResult(null);
    setSelected({ type: el.tagName.toLowerCase(), html: el.outerHTML, text: el.innerText.trim(), position: i });
  }, []);

  useEffect(() => {
    const root = contentRef.current;
    if (!root || !post) return;
    const nodes = root.querySelectorAll("p,h2,h3,h4,li,td,th,blockquote,figcaption");
    const handlers = [];
    // Neutralise links so clicking inside content never navigates the SPA away.
    root.querySelectorAll("a").forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noreferrer");
      const stop = (e) => e.stopPropagation();
      link.addEventListener("click", stop);
      handlers.push([link, stop]);
    });
    nodes.forEach((el, i) => {
      if (!el.textContent.trim() || el.querySelector("p,li,table")) return;
      el.classList.add("ez-block");
      el.setAttribute("title", "Analyze this content");
      el.setAttribute("data-testid", "content-block");
      const h = (e) => { e.stopPropagation(); selectBlock(el, i); };
      el.addEventListener("click", h);
      handlers.push([el, h]);
    });
    return () => handlers.forEach(([el, h]) => el.removeEventListener("click", h));
  }, [post, selectBlock]);

  const runFactCheck = async () => {
    if (!selected) return;
    setChecking(true); setResult(null);
    try {
      const { data } = await client.post("/factcheck", {
        post_id: Number(id), block_type: selected.type, block_html: selected.html,
        block_text: selected.text, position: selected.position,
        instruction: command.trim() || null,
      });
      setResult(data);
    } catch (e) { toast.error(apiErr(e, "Fact-check failed")); } finally { setChecking(false); }
  };

  const refetch = async () => { const { data } = await client.get(`/proposals/${result.id}`); setResult(data); };

  const doAct = async (action, thenPublish = false) => {
    setActing(true);
    try {
      if (action === "reject") { await client.post(`/proposals/${result.id}/reject`, { note }); toast.success("Rejected"); }
      if (action === "approve" || thenPublish) { await client.post(`/proposals/${result.id}/approve`, { note }); }
      if (thenPublish) {
        const { data } = await client.post(`/proposals/${result.id}/publish`);
        if (data.status === "CONTENT_CHANGED") toast.error(data.message);
        else if (data.status === "PUBLISHED_VERIFIED") toast.success("Published + Verified");
        else if (data.status === "SIMULATED_DRY_RUN") toast.success("Dry run simulated — safety checks passed");
        else if (data.status === "PUBLISH_VERIFICATION_FAILED") toast.error("Publish verification failed");
      } else if (action === "approve") toast.success("Approved");
      setNote("");
      await refetch();
    } catch (e) { toast.error(apiErr(e)); } finally { setActing(false); }
  };

  if (loading) return <Spinner label="Loading post…" />;
  if (!post) return <div className="text-slate-500">Post not found.</div>;

  const fc = result?.factcheck;
  const a = result?.analysis || {};
  const canApprove = user?.permissions?.approve;
  const canPublish = user?.permissions?.publish;

  return (
    <div className="max-w-7xl">
      <button onClick={() => navigate("/wordpress")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Back to Posts</button>

      {/* Article info */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-serif text-2xl font-bold text-slate-900" dangerouslySetInnerHTML={{ __html: safeHtml(post.title) }} />
            <a href={post.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1">{post.url} <ExternalLink className="h-3 w-3" /></a>
          </div>
          <div className="text-right text-xs text-slate-500 space-y-1">
            <div>Post <span className="font-mono">#{post.id}</span> · {post.status}</div>
            <div>Modified {(post.modified || "").slice(0, 10)}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Content */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 mb-3 text-sm text-slate-500">
            <MousePointerSquareDashed className="h-4 w-4 text-blue-600" />
            Hover any paragraph, heading, list item or table cell and click to select it for fact-checking.
          </div>
          <div data-testid="post-detail-content" ref={contentRef} className="ez-content ez-scroll max-h-[70vh] overflow-y-auto rounded-xl border border-slate-200 bg-white p-6 prose prose-sm max-w-none text-slate-700"
            dangerouslySetInnerHTML={{ __html: safeHtml(post.content_rendered || post.content) }} />
        </div>

        {/* Fact-check panel */}
        <div className="lg:col-span-5">
          <div className="sticky top-24 space-y-4">
            {!selected && (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-slate-400">
                <FileText className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                Select a block of content to begin an AI fact check.
              </div>
            )}

            {selected && (
              <div data-testid="selected-content-box" className="rounded-xl border-2 border-blue-300 bg-blue-50/40 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono uppercase tracking-widest text-blue-700">Selected Content · {selected.type}</span>
                  <button onClick={() => { if (lastEl.current) lastEl.current.classList.remove("ez-selected"); setSelected(null); setResult(null); }} className="text-xs text-slate-400 hover:text-slate-600">clear</button>
                </div>
                <p className="text-sm text-slate-700 max-h-32 overflow-y-auto ez-scroll">{selected.text}</p>
                <label className="mt-3 block text-[11px] font-mono uppercase tracking-widest text-blue-700">Instruction (what to research & change)</label>
                <textarea
                  data-testid="research-command-input"
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  rows={2}
                  placeholder="e.g. Change the session from 2026 to 2027 and verify the dates against the official source"
                  className="mt-1 w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 resize-none"
                />
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {["Change the session from 2026 to 2027", "Verify all dates against the official source", "Update fees to the latest figures"].map((c) => (
                    <button key={c} type="button" data-testid="command-chip" onClick={() => setCommand(c)} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-600 hover:border-blue-400 hover:text-blue-700">{c}</button>
                  ))}
                  {presets.map((p) => (
                    <span key={p.id} data-testid="saved-preset-chip" className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 pl-2.5 pr-1 py-1 text-[11px] text-indigo-700">
                      <button type="button" onClick={() => setCommand(p.text)} className="hover:underline">{p.text}</button>
                      <button type="button" data-testid="delete-preset-btn" onClick={() => deletePreset(p.id)} className="rounded-full p-0.5 hover:bg-indigo-100" title="Remove preset"><XCircle className="h-3 w-3" /></button>
                    </span>
                  ))}
                  {command.trim() && (
                    <button type="button" data-testid="save-preset-btn" onClick={savePreset} className="rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:border-emerald-500">＋ Save</button>
                  )}
                </div>
                <button data-testid="research-verify-btn" onClick={runFactCheck} disabled={checking} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
                  <Search className="h-4 w-4" /> {checking ? "Researching authoritative sources…" : "Research & Verify"}
                </button>
              </div>
            )}

            {checking && <div className="rounded-xl border border-slate-200 bg-white p-4"><Spinner label="Checking official sources (NTA & others)…" /></div>}

            {result && fc && (
              <div data-testid="factcheck-result" className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <VerifiedStatusBadge status={fc.verified_status} />
                  <SafetyBadge status={result.status} />
                </div>

                <div>
                  <div className="text-[11px] font-mono uppercase text-slate-400 mb-1">Existing EntranceZone Claim</div>
                  <p className="text-sm text-slate-800">{a.fact || result.portion_scope}</p>
                </div>

                {fc.current_verified_information && (
                  <div>
                    <div className="text-[11px] font-mono uppercase text-slate-400 mb-1">Current Verified Information</div>
                    <p className="text-sm text-slate-800">{fc.current_verified_information}</p>
                  </div>
                )}

                {fc.explanation && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <div className="flex items-center justify-between mb-1"><span className="text-[11px] font-mono uppercase text-slate-400">Explanation</span><ConfidenceBar value={fc.confidence} /></div>
                    <p className="text-sm text-slate-700">{fc.explanation}</p>
                  </div>
                )}

                {/* Sources */}
                {fc.sources?.length > 0 && (
                  <div>
                    <div className="text-[11px] font-mono uppercase text-slate-400 mb-2">Source Evidence ({fc.sources.length})</div>
                    <div className="space-y-2">
                      {fc.sources.map((s, i) => (
                        <div key={s.url || s.source_url || `${s.title}-${i}`} data-testid="evidence-source-card" className="rounded-lg border border-slate-200 p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${TYPE_COLORS[s.type] || TYPE_COLORS.Other}`}>{s.type}</span>
                            <ConfidenceBar value={s.confidence} />
                          </div>
                          <div className="text-sm font-semibold text-slate-800">{s.name}</div>
                          {s.explanation && <p className="text-xs text-slate-500 mt-1">{s.explanation}</p>}
                          <div className="mt-2 rounded bg-slate-50 border border-slate-200 px-2 py-1.5 text-[11px] text-slate-500 flex items-start gap-1.5">
                            <Quote className="h-3 w-3 mt-0.5 text-slate-400" /> Source evidence available — exact quotation could not be reliably extracted.
                          </div>
                          <div className="mt-2 flex items-center justify-between text-xs">
                            <span className="text-slate-400">{s.date || "date n/a"} · retrieved {(result.created_at || "").slice(0, 10)}</span>
                            {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">Open Source <ExternalLink className="h-3 w-3" /></a>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* States */}
                {result.status === "NO_CHANGE" && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 font-semibold">🟢 VERIFIED — No change required. The selected content matches the latest authoritative source.</div>
                )}
                {result.status === "MANUAL_VERIFICATION_REQUIRED" && (
                  <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">🟡 Unverified — manual verification required before any change.</div>
                )}
                {result.status === "BLOCKED_CONFLICT" && (
                  <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">⚠️ Source conflict — <b>BLOCKED, human verification required.</b> No publish path while unresolved.</div>
                )}
                {result.protected_blocks?.length > 0 && (
                  <div className="rounded-lg border border-orange-300 bg-orange-50 p-3 text-sm text-orange-800 flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5" /> Manual edit required — touches protected block(s): {result.protected_blocks.join(", ")}.</div>
                )}
                {result.validation_error && !result.diff && result.status === "MANUAL_EDIT_REQUIRED" && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">{result.validation_error}</div>
                )}

                {/* Change + diff */}
                {a.change_detected && result.diff && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 text-center">
                      <div className="text-[11px] font-mono uppercase text-red-700 mb-1">🔴 Change Required</div>
                      <div className="flex items-center justify-center gap-3 text-sm font-semibold">
                        <span className="text-slate-500 line-through">{a.existing_value}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-emerald-700">{a.proposed_value}</span>
                      </div>
                    </div>
                    <DiffView diff={result.diff} />
                  </div>
                )}

                {/* Approval */}
                {(result.status === "READY" || result.status === "APPROVED") && (
                  <div className="border-t border-slate-100 pt-4 space-y-3">
                    {result.status === "READY" && (
                      <textarea data-testid="approval-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional reviewer note (recorded in the audit log)…" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 resize-none" />
                    )}
                    <div className="flex flex-wrap gap-2">
                      {result.status === "READY" && (
                        <>
                          <button data-testid="reject-btn" disabled={!canApprove || acting} onClick={() => doAct("reject")} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-rose-400 hover:text-rose-600 disabled:opacity-40"><XCircle className="h-4 w-4" /> Reject</button>
                          <button data-testid="approve-btn" disabled={!canApprove || acting} onClick={() => doAct("approve")} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40"><CheckCircle2 className="h-4 w-4" /> Approve</button>
                        </>
                      )}
                      <button data-testid="approve-publish-btn" disabled={!canPublish || acting || !mode || mode === "UNKNOWN"} onClick={() => doAct("approve", true)} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-40 ${mode === "LIVE" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}`}>
                        <UploadCloud className="h-4 w-4" /> {mode === "LIVE" ? "Approve & Publish" : mode === "DRY_RUN" ? "Approve & Simulate (Dry Run)" : "Checking mode…"}
                      </button>
                    </div>
                    {!canApprove && <p className="text-xs text-slate-400">Your role cannot approve/publish.</p>}
                    {wp?.read_only && mode === "LIVE" && <p className="text-xs text-amber-600">LIVE publishing is disabled — WordPress connection is read-only.</p>}
                  </div>
                )}

                {["PUBLISHED_VERIFIED", "SIMULATED_DRY_RUN"].includes(result.status) && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> {result.status === "PUBLISHED_VERIFIED" ? "Published + Verified" : "Dry-run simulated + verified"} by {result.published_by}.</div>
                )}
                {result.status === "CONTENT_CHANGED" && (
                  <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-center justify-between gap-2">
                    <span>Content changed since analysis.</span>
                    <button onClick={() => { setResult(null); setSelected(null); }} className="inline-flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-xs font-semibold"><RefreshCw className="h-3 w-3" /> Refresh & Re-Analyze</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
